import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/** AuthCallback
 * Reads `session_id` from the URL hash after Emergent Google Auth redirect,
 * exchanges it with our backend for a JWT, and routes the user in.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(m[1]);

    (async () => {
      try {
        const { data } = await api.post("/auth/google-session", { session_id: sessionId });
        localStorage.setItem("au_token", data.token);
        setUser(data.user);
        // Clear the hash
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
        setError(msg);
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-6">
      <div className="panel-heavy p-10 max-w-md w-full text-center" data-testid="auth-callback">
        {error ? (
          <>
            <div className="label-tech text-brand-danger mb-3">Access denied</div>
            <div className="display font-black text-2xl uppercase tracking-tighter mb-4">
              Sign-in blocked
            </div>
            <p className="text-sm text-ink-muted mb-6">{error}</p>
            <button
              className="btn-primary"
              onClick={() => navigate("/login", { replace: true })}
              data-testid="back-to-login"
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <div className="label-tech mb-3">Authenticating</div>
            <div className="display font-black text-2xl uppercase tracking-tighter">
              Signing you in…
            </div>
            <p className="text-sm text-ink-muted mt-3">Verifying your Google account.</p>
          </>
        )}
      </div>
    </div>
  );
}
